import { dynamicImportWithRetry } from "@fatso83/retry-dynamic-import";
import type { Commands, Route, RouteContext } from "@vaadin/router";
import { defineCustomElement } from "@whilecat/core/utils/custom-elements-utils.js";
import { redirectTo, routeTo404, routeToLogin, routeToSubscriptions } from "@whilecat/core/utils/location-utils.ts";
import {
    isAdmin,
    isAuthenticated,
    isNotAuthenticated,
    logout,
    redirectToLoginOnUnauthorized,
} from "@whilecat/services/authentication-service.ts";
import { checkIfLevelAccessible } from "@whilecat/services/user-course-service.ts";
import { makeLevels } from "./core/elements/course-levels/base-course-levels.ts";

export const ROUTES: Route[] = [
    {
        path: "/",
        component: "landing-page",
        action: async () => {
            await dynamicImportWithRetry(() => import("./pages/landing/landing.ts"));
        },
    },
    {
        path: "/login",
        component: "login-form",
        action: async () => {
            if (isAuthenticated()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/security/login-form.ts"));
        },
    },
    {
        path: "/logout",
        action: async () => {
            if (isAuthenticated()) {
                await logout();
                return redirectTo("/login");
            }
        },
    },
    {
        path: "/admin",
        component: "admin-area",
        action: async () => {
            redirectToLoginOnUnauthorized();
            if (!isAdmin()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/admin/admin-area.ts"));
        },
    },
    {
        path: "/registration",
        component: "registration-form",
        action: async () => {
            if (isAuthenticated()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/security/registration-form.ts"));
        },
    },
    {
        path: "/restore-password",
        component: "forgot-password-form",
        action: async () => {
            if (isAuthenticated()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/security/forgot-password-form.ts"));
        },
    },
    {
        path: "/reset-password",
        component: "reset-password",
        action: async () => {
            if (isAuthenticated()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/security/reset-password.ts"));
        },
    },
    {
        path: "/confirm",
        component: "confirm-registration-form",
        action: async () => {
            if (isAuthenticated()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/security/confirm-registration-form.ts"));
        },
    },
    {
        path: "/authentication/github",
        component: "external-authentication-processing",
        action: async () => {
            if (isAuthenticated()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/security/external-authentication-processing.ts"));
        },
    },
    {
        path: "/authentication/google",
        component: "external-authentication-processing",
        action: async () => {
            if (isAuthenticated()) {
                return redirectTo("/courses");
            }
            await dynamicImportWithRetry(() => import("./pages/security/external-authentication-processing.ts"));
        },
    },
    {
        path: "/courses",
        component: "course-list",
        action: async () => {
            await dynamicImportWithRetry(() => import("./pages/courses/course-list.ts"));
        },
    },
    {
        path: "/prices",
        component: "prices-block",
        action: async () => {
            await dynamicImportWithRetry(() => import("./pages/prices/prices-block.js"));
        },
    },
    {
        path: "/repetition",
        component: "repetition-page",
        action: async (context: RouteContext, commands: Commands) => {
            if (isNotAuthenticated()) {
                return routeToLogin(commands);
            }
            await dynamicImportWithRetry(() => import("./pages/repetition/repetition.ts"));
        },
    },
    {
        path: "/courses/:section/:course",
        action: async (context: RouteContext, commands: Commands) => {
            const courseName = context.params.course as string;
            const section = context.params.section;

            var course;
            try {
                course = await dynamicImportWithRetry(() => import(`./content/${section}/${courseName}/levels.ts`));
            } catch (error) {
                console.error(error);
                return routeTo404(commands);
            }
            defineCustomElement(
                `${section}-${courseName}-levels`,
                makeLevels(
                    course.getCourseUuid(),
                    course.getCourseName(),
                    course.getCourseDescription(),
                    course.getIconPath(),
                ),
            );

            return commands.component(`${section}-${courseName}-levels`);
        },
    },
    {
        path: "/courses/:section/:course/lesson-:lessonNumber",
        action: async (context: RouteContext, commands: Commands) => {
            const courseName = context.params.course as string;
            const section = context.params.section;
            const lessonNumber = context.params.lessonNumber;

            if (isNotAuthenticated()) {
                return routeToLogin(commands);
            }

            var course, lesson;
            try {
                course = await dynamicImportWithRetry(() => import(`./content/${section}/${courseName}/levels.ts`));
                lesson = await dynamicImportWithRetry(
                    () => import(`./content/${section}/${courseName}/lesson-${lessonNumber}.ts`),
                );
            } catch (error) {
                console.error(error);
            }

            // A fake JS module is sent by Caddy when the user is not allowed to view a lesson.
            if (lesson.isNotAccessible) {
                console.debug("Lesson is not accessible", lesson);
                return routeToSubscriptions(commands);
            }

            const accessible = await checkIfLevelAccessible(course.getCourseUuid(), lesson.getLevelUuid());
            if (!accessible) {
                console.debug("Lesson is not accessible yet", lesson);
                return routeTo404(commands);
            }

            try {
                await dynamicImportWithRetry(
                    () => import(`./content/${section}/${courseName}/lesson-${lessonNumber}.ts`),
                );
            } catch (error) {
                console.error(error);
                return routeTo404(commands);
            }

            if (Object.hasOwn(lesson, "getComponent")) {
                defineCustomElement(`${section}-${courseName}-lesson-${lessonNumber}`, await lesson.getComponent());
            }
            return commands.component(`${section}-${courseName}-lesson-${lessonNumber}`);
        },
    },
    {
        path: "/404",
        component: "not-found",
        action: async (context: RouteContext) => {
            console.error("Redirected to not found page: ", context);
            await dynamicImportWithRetry(() => import("./pages/404/not-found.ts"));
        },
    },
    {
        path: "/profile",
        component: "user-profile",
        action: async () => {
            await dynamicImportWithRetry(() => import("./pages/user/user-profile.ts"));
        },
    },
    {
        path: "/subscription",
        component: "subscription-form",
        action: async () => {
            await dynamicImportWithRetry(() => import("./pages/user/subscription-form.ts"));
        },
    },
    {
        path: "/contact-us",
        component: "feedback-form",
        action: async () => {
            await dynamicImportWithRetry(() => import("./pages/feedback/feedback.ts"));
        },
    },
    {
        path: "/level-viewer(.*)",
        component: "level-viewer",
        action: async (_, commands: Commands) => {
            if (isNotAuthenticated()) {
                return routeToLogin(commands);
            } else if (!isAdmin()) {
                return redirectTo("/courses");
            }

            await dynamicImportWithRetry(() => import("./dev/level-viewer.ts"));
        },
    },
    {
        path: "/session-replay-viewer",
        component: "session-replay-viewer",
        action: async () => {
            if (!isAdmin()) {
                return redirectTo("/courses");
            }

            await dynamicImportWithRetry(() => import("./dev/session-replay-viewer.ts"));
        },
    },
    {
        path: "(.*)",
        component: "not-found",
        action: async (context: RouteContext, commands: Commands) => {
            console.error("Not found page: ", context);
            return routeTo404(commands);
        },
    },
];
